import store from '../reducers/storeRegistration';
import behaviorCacheUtils from '../utils/behaviorCacheUtils';

/**
 * Module-level variable to store config behaviors
 */
let configBehaviors = {};
let readyPromise = null;
const populateFromStore = () => {
  const {
    configBehavior
  } = store.getState();
  const {
    list = []
  } = configBehavior || {};
  list.forEach(behavior => {
    configBehaviors[behavior.identifier] = behavior.code;
  });
  return configBehaviors;
};

/**
 * True when ConfigBehavior index has finished (success or fail), the module
 * cache is already populated, or the store already has rows (tests / legacy).
 */
const isConfigBehaviorsReady = () => {
  if (Object.keys(configBehaviors).length > 0) {
    return true;
  }
  const {
    configBehavior
  } = store.getState();
  // Production always has the reducer slice ({} until INDEX_*). Unit tests often
  // stub getState without configBehavior — treat that as ready so evaluate paths
  // do not wait on the 15s timeout (mocha defaults to 5s).
  if (!configBehavior) {
    return true;
  }
  if (configBehavior.indexed) {
    return true;
  }
  const {
    list = []
  } = configBehavior;
  return Array.isArray(list) && list.length > 0;
};

/**
 * Wait until ConfigBehavior index has landed (or timeout) so evaluateFormat
 * does not race an empty provider (CU-86e2np5zw).
 *
 * Resolves on timeout as well so a failed/slow index cannot block the UI
 * forever; the behaviors empty-set fix then prevents a sticky miss.
 *
 * @param {number} timeoutMs
 * @returns {Promise<void>}
 */
const ensureConfigBehaviorsReady = (timeoutMs = 15000) => {
  if (isConfigBehaviorsReady()) {
    if (Object.keys(configBehaviors).length === 0) {
      populateFromStore();
    }
    return Promise.resolve();
  }
  if (readyPromise) {
    return readyPromise;
  }
  readyPromise = new Promise(resolve => {
    let settled = false;
    let unsubscribe = () => {};
    let timeoutId;
    const finish = () => {
      if (settled) {
        return;
      }
      settled = true;
      readyPromise = null;
      if (Object.keys(configBehaviors).length === 0) {
        populateFromStore();
      }
      resolve();
    };
    unsubscribe = store.subscribe(() => {
      if (isConfigBehaviorsReady()) {
        unsubscribe();
        clearTimeout(timeoutId);
        finish();
      }
    });

    // Re-check after subscribe: INDEX_SUCCESS can land in the gap between the
    // initial isConfigBehaviorsReady() check and registration (CU-86e2np5zw).
    if (isConfigBehaviorsReady()) {
      unsubscribe();
      finish();
    } else {
      timeoutId = setTimeout(() => {
        unsubscribe();
        finish();
      }, timeoutMs);
    }
  });
  return readyPromise;
};

/**
 * Return the config behaviors map (identifier -> code).
 */
const configBehaviorsFunc = () => {
  if (Object.keys(configBehaviors).length > 0) {
    return configBehaviors;
  }
  behaviorCacheUtils.clearAllBehaviorCaches();
  return populateFromStore();
};

/**
 * Clear the behavior cache and reset the configBehaviors object
 */
const clearConfigBehaviors = () => {
  // We don't call clearAllBehaviorCaches here to avoid circular calls
  configBehaviors = {};
  readyPromise = null;
};

// Register our clear function with the utility
behaviorCacheUtils.registerClearConfigBehaviors(clearConfigBehaviors);
export { configBehaviorsFunc, clearConfigBehaviors, ensureConfigBehaviorsReady, isConfigBehaviorsReady };